// Sentry (errors) + PostHog (product analytics) — go-public observability.
// PII-HARDENED: this app handles SSNs and client financial data, so
//  - Sentry: no default PII, no session replay, request bodies never attached.
//  - PostHog: all session-recording text and inputs are MASKED before leaving
//    the browser; autocapture never captures input values by design.
// The DSN and phc token are public client-side identifiers (safe to ship).
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

const SENTRY_DSN = "https://bd2691900c2e2daf53a27af31224b27a@o4511938852487168.ingest.us.sentry.io/4511952428793856";
const POSTHOG_TOKEN = "phc_xdyekNKMFTpLHKWVhfgtA8GRPw3nLqjehe7qPctVG5DY"; // Tanya's OWN PostHog (shared with her funding app — filter by app tag)
const APP_TAG = "alief-dispute-hub";

// Routes that carry a LIVE credential in the URL itself. A password-reset,
// staff-invite, company-invite, portal set-password, intake or filing-review
// link IS the authentication — anyone holding the URL can act as that person
// until it expires. Neither PostHog nor Sentry may ever receive one, because
// their read access is far broader than the app's (and the PostHog project is
// shared with the funding app). We scrub the token out of every URL-shaped
// field before either SDK transmits it.
// Covers BOTH the SPA route paths (/reset-password/:token …) AND the API call
// URLs the frontend fires with a token in the path (/api/invite/:token,
// /api/filing-review/:token …). Sentry's fetch breadcrumbs carry the API URL,
// so missing the /api/* shapes leaks a live token even though the page route
// is redacted. `filing-review` must be listed explicitly — `filing` alone does
// not match it because of the hyphen.
const TOKEN_PATH = /(\/(?:api\/)?(reset-password|staff-invite|join|filing-review|filing|intake|invite|portal\/set-password|portal\/verify)\/)[^/?#]+/gi;
const TOKEN_QUERY = /([?&](?:token|code|t|jwt)=)[^&#]+/gi;

export function redactUrl(value: string): string {
  return value.replace(TOKEN_PATH, "$1<redacted>").replace(TOKEN_QUERY, "$1<redacted>");
}

function scrubDeep(obj: unknown, depth = 0): void {
  if (!obj || typeof obj !== "object" || depth > 4) return;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (typeof v === "string" && (v.includes("/") || v.includes("token="))) {
      (obj as Record<string, unknown>)[k] = redactUrl(v);
    } else if (typeof v === "object") {
      scrubDeep(v, depth + 1);
    }
  }
}

export function initObservability() {
  if (typeof window === "undefined") return;
  if (/^(localhost|127\.|192\.168\.)/.test(window.location.hostname)) return; // dev: stay silent

  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: false,
    tracesSampleRate: 0.2,
    initialScope: { tags: { app: APP_TAG } },
    // Scrub before send: the URL rides on the event, its request context, and
    // every breadcrumb/span, so walk the whole payload rather than one field.
    beforeSend(event) {
      scrubDeep(event);
      return event;
    },
    beforeBreadcrumb(crumb) {
      scrubDeep(crumb);
      return crumb;
    },
  });

  posthog.init(POSTHOG_TOKEN, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*", // never ship visible SSNs/report data in replays
    },
    // $current_url / $pathname / referrer are captured on every pageview and
    // autocapture event — redact the token out of all of them.
    sanitize_properties: (props) => {
      for (const k of ["$current_url", "$pathname", "$referrer", "$initial_current_url"]) {
        if (typeof props[k] === "string") props[k] = redactUrl(props[k] as string);
      }
      return props;
    },
  });
  posthog.register({ app: APP_TAG });
}
